<template lang="html">
  <form class="flex flex-col gap-4" novalidate @submit.prevent="submit">
    <UFormField label="Оценка" required :error="errors.rating">
      <div class="flex gap-1" role="radiogroup" aria-label="Оценка товара">
        <button
          v-for="i in 5"
          :key="i"
          type="button"
          role="radio"
          class="cursor-pointer"
          :aria-checked="model.rating === i"
          :aria-label="`${i} из 5`"
          @click="model.rating = i"
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            :fill="i <= model.rating ? 'currentColor' : 'none'"
            :class="['size-7', i <= model.rating ? 'text-primary' : 'text-dimmed']"
          >
            <path :d="STAR_PATH" />
          </svg>
        </button>
      </div>
    </UFormField>

    <UFormField label="Достоинства" :error="errors.pros">
      <UTextarea v-model="model.pros" class="w-full" :rows="2" :maxlength="REVIEW_FIELD_MAX_LENGTH" />
    </UFormField>
    <UFormField label="Недостатки" :error="errors.cons">
      <UTextarea v-model="model.cons" class="w-full" :rows="2" :maxlength="REVIEW_FIELD_MAX_LENGTH" />
    </UFormField>
    <UFormField label="Комментарий" :error="errors.text">
      <UTextarea v-model="model.text" class="w-full" :rows="4" :maxlength="REVIEW_FIELD_MAX_LENGTH" />
    </UFormField>

    <UFormField
      v-if="props.config.allowMedia || model.resourceIds.length > 0"
      :label="photosLabel"
      :error="errors.resourceIds || uploadError"
    >
      <div class="flex flex-wrap gap-2">
        <div v-for="(id, index) in model.resourceIds" :key="id" class="relative size-20 border border-default">
          <UiPicture :src="id" variant="imageFull" :alt="`Фото ${index + 1}`" sizes="80px" class="block size-full" />
          <UButton
            icon="i-lucide-x"
            size="xs"
            color="neutral"
            class="absolute top-1 right-1"
            :aria-label="`Удалить фото ${index + 1}`"
            @click="removePhoto(id)"
          />
        </div>
        <button
          v-if="slotsLeft > 0"
          type="button"
          class="flex size-20 cursor-pointer items-center justify-center border border-dashed border-default text-muted hover:text-default disabled:cursor-wait"
          :disabled="uploading"
          aria-label="Добавить фото"
          @click="fileInput?.click()"
        >
          <UIcon :name="uploading ? 'i-lucide-loader-circle' : 'i-lucide-image-plus'" :class="['size-6', uploading && 'animate-spin']" />
        </button>
      </div>
      <input ref="fileInput" type="file" accept="image/*" multiple class="hidden" @change="onFilesSelected">
    </UFormField>

    <UCheckbox
      v-model="model.hideAuthorName"
      label="Не показывать моё имя"
      description="В отзыве будет написано «Покупатель»"
    />

    <div v-if="props.apiError" class="flex flex-col items-start gap-1" role="alert">
      <p class="text-sm text-error">
        {{ props.apiError }}
      </p>
      <!-- Переход закрывает модалку: форма на другой странице не нужна -->
      <UButton
        v-if="props.apiErrorLink"
        :to="props.apiErrorLink.to"
        :label="props.apiErrorLink.label"
        variant="link"
        size="sm"
        class="px-0"
        @click="emit('cancel')"
      />
    </div>

    <div class="flex justify-end gap-2">
      <UButton variant="ghost" color="neutral" label="Отмена" @click="emit('cancel')" />
      <UButton type="submit" label="Отправить" :loading="props.loading || uploading" />
    </div>
  </form>
</template>

<script setup lang="ts">
import { STAR_PATH } from '../config/star'
import {
  photoSlotsLeft,
  REVIEW_FIELD_MAX_LENGTH,
  validateReviewForm,
  type ReviewFormConfig,
  type ReviewFormField,
  type ReviewFormModel,
} from '../utils/reviewFormModel'

import UiPicture from '~/app/components/Image/UiPicture.vue'

/**
 * Форма отзыва: оценка, тексты, фото, скрытие имени. Отправку, ошибки API и загрузку файла
 * делает вызывающий (useReviewFormModal), форма только держит черновик и проверяет его.
 */
const props = defineProps<{
  /** Начальное значение формы (контракт useFormModal); форма правит свою копию */
  writeModel: ReviewFormModel
  /** Контракт useFormModal, форме не нужен */
  readModel?: unknown
  /** Контракт useFormModal, форме не нужен */
  prevModel?: unknown
  config: ReviewFormConfig
  /** Загрузить файл и вернуть id ресурса */
  upload: (file: File) => Promise<string>
  loading?: boolean
  /** Общая ошибка отправки */
  apiError?: string
  /** Ссылка под общей ошибкой (например, «Перейти в «Мои отзывы»» при уже оставленном отзыве) */
  apiErrorLink?: { label: string, to: string }
  /** Ошибки сервера по полям формы */
  apiFieldErrors?: Partial<Record<ReviewFormField, string>>
}>()
const emit = defineEmits<{ submit: [model: ReviewFormModel], cancel: [] }>()

const model = reactive<ReviewFormModel>({ ...props.writeModel, resourceIds: [...props.writeModel.resourceIds] })
const fileInput = useTemplateRef('fileInput')
const uploading = ref(false)
const uploadError = ref<string>()
/** Ошибки показываются после первой попытки отправки и дальше пересчитываются на лету */
const submitted = ref(false)

const clientErrors = computed(() => (submitted.value ? validateReviewForm(model, props.config) : {}))
const errors = computed(() => ({ ...props.apiFieldErrors, ...clientErrors.value }))
const slotsLeft = computed(() => photoSlotsLeft(model.resourceIds, props.config))
// Без allowMedia блок виден, только если в редактируемом отзыве остались фото: их можно убрать
const photosLabel = computed(() => (props.config.allowMedia ? `Фото (до ${props.config.maxPhotos})` : 'Фото'))

async function onFilesSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const selected = Array.from(input.files ?? [])
  // Сбрасываем, чтобы повторный выбор того же файла снова вызвал change
  input.value = ''

  const accepted = selected.slice(0, slotsLeft.value)
  uploadError.value = accepted.length < selected.length
    ? `Можно добавить не больше ${props.config.maxPhotos} фото`
    : undefined
  if (!accepted.length) return

  uploading.value = true
  try {
    for (const file of accepted) model.resourceIds.push(await props.upload(file))
  } catch {
    uploadError.value = 'Не удалось загрузить фото. Попробуйте ещё раз'
  } finally {
    uploading.value = false
  }
}

function removePhoto(id: string) {
  model.resourceIds = model.resourceIds.filter(resourceId => resourceId !== id)
  uploadError.value = undefined
}

function submit() {
  submitted.value = true
  if (uploading.value || Object.keys(clientErrors.value).length > 0) return
  emit('submit', { ...model, resourceIds: [...model.resourceIds] })
}
</script>
